import { Banner } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { FBCommentGuardSlideoutBanner } from '@mm/icons/SlideoutBanners';
import { useChatStartersCRUDActions } from '@mm/state/actions/ChatStartersCRUDActinos';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { selectPromoterById } from '@mm/state/selectors/promoterSelector';
import {
  activeChatbotIdSelector,
  chatbotIsActiveOnConnectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import { DialoguePreview } from '../../../../../modules/DailoguePreview/DialoguePreview';
import { useShowInactiveBannerFlag } from '../../../../common/hooks/use-show-inactive-bot-banner-flag';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import FrequencyCapControl from '../../../../features/startBotPromoters/commentGuards/components/CommentGuardForm/FrequencyCapControl';
import { EditFBPost } from '../../../../features/startBotPromoters/commentGuards/components/EditFBPost/EditFBPost';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './FBCommentGuardSlideoutEditor.module.css';

export interface FBCommentGuardSlideoutEditorProps {
  promoterId: number;
}

export const FBCommentGuardSlideoutEditor = ({
  promoterId,
}: FBCommentGuardSlideoutEditorProps) => {
  const chatbotId = useSelector(activeChatbotIdSelector);

  const promoter = useSelector(state => selectPromoterById(state, promoterId));

  const promoterCRUDActions = usePromoterCRUDActions();

  const chatbotIsActiveOnConnectedPage = useSelector(
    chatbotIsActiveOnConnectedPageSelector,
  );
  const { onClickActivate } = useShowInactiveBannerFlag();

  const { openActivateInactiveBotModal } = useModalActions();

  const { retrieveAll } = useChatStartersCRUDActions();

  const page = useSelector(state =>
    pageEntityByIdSelector(state, String(promoter?.pageId)),
  );
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const [isNameEditing, setIsNameEditing] = useState(false);
  const [promoterNewName, setPromoterNewName] = useState(
    promoter?.name || 'Trigger Custom Name',
  );
  const { setRef } = useSlideoutOnClose();

  useEffect(() => {
    if (promoter?.name && promoter?.name !== promoterNewName) {
      setPromoterNewName(promoter.name);
    }
  }, [promoter?.name]);

  let orderNumber = 0;

  const nextOrderNumber = () => {
    orderNumber += 1;
    return orderNumber.toString();
  };

  const saveName = async () => {
    const newName = promoterNewName.trim();
    setPromoterNewName(newName);
    setIsNameEditing(false);
    if (promoter && newName && newName !== promoter.name) {
      await promoterCRUDActions.update(chatbotId, {
        id: promoter.id,
        name: newName,
      });

      retrieveAll({
        botId: chatbotId,
        kind: 'fb_comment_guard',
      });
    }
  };
  const customActivateAction = async () => {
    if (promoter) {
      const updatePromoter = async () => {
        await promoterCRUDActions.update(chatbotId, {
          id: promoter.id,
          active: !promoter.active,
        });
        retrieveAll({
          botId: chatbotId,
          kind: 'fb_comment_guard',
        });
      };

      if (!chatbotIsActiveOnConnectedPage && !promoter.active) {
        openActivateInactiveBotModal({
          onBotActivateConfirm: () =>
            setTimeout(() => onClickActivate(updatePromoter), 500),
          useCustomOnBotActivateConfirm: true,
        });
      } else {
        updatePromoter();
      }
    }
  };

  if (promoter && page) {
    return (
      <SlideoutMenu className={styles.menu} setRef={setRef}>
        <PromoterHeader
          promoter={promoter}
          customActivateAction={customActivateAction}
          title="Facebook Comment Autoresponder"
        />

        <FreeTrialWarning />

        <SlideoutMenu.Content className={styles.content}>
          {!isMobile && (
            <Banner
              css={{
                container: styles.bannerContainer,
                header: styles.bannerHeader,
                collapsedHeader: styles.collapsedHeader,
                content: styles.bannerContent,
              }}
              withBannerHeader
              headerText="What is Facebook Comment Autoresponder?"
              withLeftArea={false}
              showCloseIcon
              usedInSlideout
            >
              <FBCommentGuardSlideoutBanner textColor="white" />
            </Banner>
          )}
          <div className={styles.container}>
            <div className={styles.configArea}>
              {
                // Note: commenting out fo now since the requirement to edit is constantly being added/removed
                /* {isNameEditing ? (
              <div className={styles.nameContainer}>
                <div>
                  <Input
                    css={{
                      container: styles.nameInputContainer,
                      input: styles.nameInput,
                    }}
                    value={promoterNewName}
                    onChange={e => setPromoterNewName(e.currentTarget.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        saveName();
                      }
                    }}
                  />
                </div>
                <div className={styles.nameButton}>
                  <Button
                    disabled={
                      !promoterNewName || promoterNewName === promoter.name
                    }
                    onClick={saveName}
                    css={{ container: styles.nameButtonContainer }}
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className={styles.nameContainer}>
                <div className={styles.nameContainerText}>{promoter.name}</div>
                <EditIcon
                  className={styles.nameIcon}
                  color={colors.gray}
                  size={18}
                  onClick={() => {
                    setIsNameEditing(true);
                  }}
                />
              </div>
            )} */
              }
              <CollapsibleContentContainer
                orderNumber={nextOrderNumber()}
                containerName="Choose whether to apply this comment autoresponder to a single Facebook post or to all"
                className={styles.collapsibleContainer}
                useCircleNumberStyle
                headerClassName={styles.collapsibleContainerHeader}
                childrenContainerClassName={styles.collapsibleChildrenContainer}
                stepperStylingPlatform="facebook"
              >
                <EditFBPost guard={promoter} botId={chatbotId} />
              </CollapsibleContentContainer>

              <CollapsibleContentContainer
                orderNumber={nextOrderNumber()}
                containerName="The Dialogue for users who engage with your Comment Autoresponder"
                className={styles.collapsibleContainer}
                useCircleNumberStyle
                headerClassName={styles.collapsibleContainerHeader}
                childrenContainerClassName={styles.collapsibleChildrenContainer}
                stepperStylingPlatform="facebook"
              >
                <EmbeddableDialogueEditorWithPreview
                  editPaneClassname={styles.dialogueEditPane}
                  previewClassName={styles.dialogueEditPreviewPane}
                  dialogueId={page.id}
                  usedInSlideout
                  hideDialogueSelector
                  firstWidgetNotAllowedWarning={{
                    showWarning: true,
                    message: `This widget type is NOT supported as the first message in your Facebook Comment Autoresponder. 
                      Please use a Quick Question widget instead.`,
                    startbotType: 'facebook_cg',
                  }}
                  platforms={['facebook']}
                />
              </CollapsibleContentContainer>
              <CollapsibleContentContainer
                orderNumber={nextOrderNumber()}
                containerName="Specify a Frequency Cap for Comment Replies"
                className={styles.collapsibleContainer}
                useCircleNumberStyle
                headerClassName={styles.collapsibleContainerHeader}
                childrenContainerClassName={
                  styles.collapsibleChildrenFrequencyCapContainer
                }
                stepperStylingPlatform="facebook"
              >
                <FrequencyCapControl
                  guard={promoter}
                  chatbotId={chatbotId}
                  usedInSlideout
                />
              </CollapsibleContentContainer>
            </div>
            {!isMobile && (
              <DialoguePreview
                dialogueId={page.id}
                className={styles.dialoguePreview}
                sticky
              />
            )}
          </div>
        </SlideoutMenu.Content>
      </SlideoutMenu>
    );
  }
  return null;
};
